@if (isPreview) {
  <p class="mb-6 rounded-2xl border border-outline-variant bg-surface-container px-4 py-3 text-sm text-on-surface">
    Preview of last saved challenge — save Overview to refresh.
  </p>
}

<img
  [src]="coverSrc()"
  [alt]="challenge.title + ' cover'"
  class="mb-4 aspect-[16/9] w-full max-w-3xl rounded-2xl object-cover"
  (error)="onImageError($event, 'cover')"
/>

@if (challenge.sponsors.length) {
  <div class="sponsor-strip mb-6 max-w-3xl">
    <p class="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-on-surface-variant">Sponsored by</p>
    <div class="mt-2 flex flex-wrap items-center gap-x-5 gap-y-3">
      @for (s of challenge.sponsors; track s.id) {
        @if (s.websiteUrl) {
          <a
            class="sponsor-item"
            [href]="s.websiteUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              [src]="sponsorSrc(s)"
              [alt]="s.name"
              class="h-8 w-8 rounded-md object-cover"
              (error)="onImageError($event, 'sponsor')"
            />
            <span>{{ s.name }}</span>
          </a>
        } @else {
          <div class="sponsor-item">
            <img
              [src]="sponsorSrc(s)"
              [alt]="s.name"
              class="h-8 w-8 rounded-md object-cover"
              (error)="onImageError($event, 'sponsor')"
            />
            <span>{{ s.name }}</span>
          </div>
        }
      }
    </div>
  </div>
}

<p class="text-xs font-extrabold uppercase tracking-[0.24em] text-secondary">
  {{ challenge.type }} · {{ challengeStatusLabel }}
</p>
@if (showRegistrationStatusLine || participationSummary) {
  <p class="mt-2 text-sm font-semibold text-on-surface-variant">
    @if (showRegistrationStatusLine) {
      <span>{{ registrationStatusLabel }}</span>
    }
    @if (showRegistrationStatusLine && participationSummary) {
      <span> · </span>
    }
    @if (participationSummary) {
      <span>{{ participationSummary }}</span>
    }
  </p>
}
<h1 class="mt-3 font-display text-4xl font-extrabold tracking-tight text-primary md:text-5xl">
  {{ challenge.title }}
</h1>
@if (challenge.summary) {
  <p class="mt-4 text-lg text-on-surface-variant">{{ challenge.summary }}</p>
}
@if (showFreeRemediationBenefit) {
  <p class="mt-4 rounded-2xl bg-surface-container px-4 py-3 text-sm text-on-surface">
    <span class="font-semibold text-primary">SahkaarX participation prize:</span>
    scored participants with skill gaps get a free remediation learning plan for this challenge's skills.
  </p>
}
@if (ipPolicyLabel) {
  <p class="mt-3 text-sm font-semibold text-on-surface-variant">IP: {{ ipPolicyLabel }}</p>
}

<div class="mt-6 flex flex-wrap gap-2">
  @if (audienceRoleLabel) {
    <span class="rounded-full bg-surface-container px-3 py-1 text-xs font-semibold text-on-surface">
      {{ audienceRoleLabel }}
    </span>
  }
  @if (audienceSpecializationLabel) {
    <span class="rounded-full bg-surface-container px-3 py-1 text-xs font-semibold text-on-surface">
      {{ audienceSpecializationLabel }}
    </span>
  }
</div>

@if (keyDateRows.length) {
  <div class="mt-8">
    <h2 class="text-sm font-bold uppercase tracking-wide text-on-surface-variant">Key dates</h2>
    <dl class="mt-3 grid gap-2 sm:grid-cols-2">
      @for (row of keyDateRows; track row.label) {
        <div class="rounded-2xl border border-outline-variant bg-white px-4 py-3">
          <dt class="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{{ row.label }}</dt>
          <dd class="mt-1 text-sm font-semibold text-on-surface">{{ row.value }}</dd>
        </div>
      }
    </dl>
  </div>
}

@if (!isPreview) {
  <div class="mt-6">
    <ng-content select="[challengeNextSteps]"></ng-content>
  </div>
}

@if (!isPreview) {
  <div class="mt-8">
    <ng-content select="[challengeActions]"></ng-content>
  </div>
}

<div class="brief-tabs mt-10" role="tablist" aria-label="Challenge sections">
  <button
    type="button"
    role="tab"
    class="brief-tab"
    [class.active]="activeTab === 'overview'"
    [attr.aria-selected]="activeTab === 'overview'"
    (click)="selectTab('overview')"
  >
    Overview
  </button>
  <button
    type="button"
    role="tab"
    class="brief-tab"
    [class.active]="activeTab === 'skills'"
    [attr.aria-selected]="activeTab === 'skills'"
    (click)="selectTab('skills')"
  >
    Skills covered
  </button>
  <button
    type="button"
    role="tab"
    class="brief-tab"
    [class.active]="activeTab === 'faq'"
    [attr.aria-selected]="activeTab === 'faq'"
    (click)="selectTab('faq')"
  >
    FAQ
  </button>
  @if (showResultsTab) {
    <button
      type="button"
      role="tab"
      class="brief-tab"
      [class.active]="activeTab === 'results'"
      [attr.aria-selected]="activeTab === 'results'"
      (click)="selectTab('results')"
    >
      Results
    </button>
  }
</div>

@if (activeTab === 'overview') {
  <div class="mt-6" role="tabpanel">
    @if (descriptionHtml) {
      <div class="max-w-3xl text-sm leading-7 text-on-surface lesson-md" [innerHTML]="descriptionHtml"></div>
    } @else {
      <p class="text-sm text-on-surface-variant">No problem statement yet.</p>
    }

    @if (challenge.judges?.length) {
      <div class="mt-8">
        <h2 class="text-sm font-bold uppercase tracking-wide text-on-surface-variant">Judges</h2>
        <div class="mt-3 flex flex-wrap gap-6">
          @for (j of challenge.judges; track j.id) {
            <div class="max-w-xs">
              <img
                [src]="judgeSrc(j)"
                [alt]="j.name"
                class="mb-2 h-16 w-16 rounded-full object-cover"
                (error)="onImageError($event, 'judge')"
              />
              <p class="text-sm font-semibold text-on-surface">{{ j.name }}</p>
              @if (j.qualification) {
                <p class="text-xs text-on-surface-variant">{{ j.qualification }}</p>
              }
              @if (j.summary) {
                <p class="mt-1 text-xs text-on-surface-variant">{{ j.summary }}</p>
              }
            </div>
          }
        </div>
      </div>
    }

    @if (challenge.prizeTiers?.length) {
      <div class="mt-8">
        <h2 class="text-sm font-bold uppercase tracking-wide text-on-surface-variant">Prizes</h2>
        <ul class="mt-3 space-y-2">
          @for (p of challenge.prizeTiers; track p.id) {
            <li class="flex items-start gap-3 text-sm text-on-surface">
              <img
                [src]="prizeSrc(p)"
                [alt]="p.name"
                class="mt-0.5 h-10 w-10 rounded-lg object-cover"
                (error)="onImageError($event, 'prize')"
              />
              <div>
                <span class="font-semibold">{{ p.name }}</span>
                @if (p.prizeValue) { · {{ p.prizeValue }} }
                @if (p.description) {
                  <span class="block text-xs text-on-surface-variant">{{ p.description }}</span>
                }
              </div>
            </li>
          }
        </ul>
      </div>
    }

    @if (challenge.winners?.length) {
      <div class="mt-8">
        <h2 class="text-sm font-bold uppercase tracking-wide text-on-surface-variant">Winners</h2>
        <ul class="mt-3 space-y-2">
          @for (w of challenge.winners; track w.prizeTierId + '-' + w.rank) {
            <li class="text-sm text-on-surface">
              #{{ w.rank }} · {{ w.prizeName }} · {{ w.displayName || w.teamName || 'Winner' }}
              @if (w.prizeValue) { · {{ w.prizeValue }} }
            </li>
          }
        </ul>
      </div>
    }
  </div>
}

@if (activeTab === 'skills') {
  <div class="mt-6" role="tabpanel">
    @if (hasSkillMappings) {
      <ul class="skills-tree">
        @for (group of skillGroups; track group.skillId) {
          <li class="skills-tree__skill">
            <p class="skills-tree__skill-name">{{ group.skillName }}</p>
            <ul class="skills-tree__topics">
              @for (topic of group.topics; track topic.skillId + ':' + topic.topicName) {
                <li class="skills-tree__topic">
                  <span>{{ topic.topicName }}</span>
                  @if (topic.targetProficiency != null && topic.targetProficiency !== '') {
                    <span class="skills-tree__meta">({{ proficiencyLabel(topic.targetProficiency) }})</span>
                  }
                  @if (topic.source === 'fundamental') {
                    <span class="skills-tree__meta">· fundamental</span>
                  }
                </li>
              }
            </ul>
          </li>
        }
      </ul>
    } @else if (hasSkillsAssessed) {
      <ul class="skills-tree">
        @for (skill of challenge.skillsAssessed; track skill) {
          <li class="skills-tree__skill">
            <p class="skills-tree__skill-name">{{ skill }}</p>
          </li>
        }
      </ul>
    } @else {
      <p class="text-sm text-on-surface-variant">No skills mapped yet.</p>
    }
  </div>
}

@if (activeTab === 'faq') {
  <div class="mt-6" role="tabpanel">
    @if (faqItems.length) {
      <div class="space-y-2">
        @for (item of faqItems; track item.id || item.question) {
          <details class="rounded-2xl border border-outline-variant bg-white px-4 py-3">
            <summary class="cursor-pointer text-sm font-semibold text-on-surface">{{ item.question }}</summary>
            <p class="mt-2 text-sm leading-6 text-on-surface-variant whitespace-pre-wrap">{{ item.answer }}</p>
          </details>
        }
      </div>
    } @else {
      <p class="text-sm text-on-surface-variant">No FAQ items yet.</p>
    }
  </div>
}

@if (showResultsTab) {
  <div class="mt-6" role="tabpanel" [hidden]="activeTab !== 'results'">
    <ng-content select="[challengeResults]"></ng-content>
  </div>
}